/**
 * Defensive localStorage helpers. Every access is wrapped in try/catch (private mode,
 * disabled storage, quota errors) and falls back to an in-memory map so the app keeps
 * working without persistence.
 */
const PREFIX = "conquer.";
const memory = new Map<string, string>();

function storage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function readString(key: string): string | null {
  const full = PREFIX + key;
  const s = storage();
  if (s) {
    try {
      const v = s.getItem(full);
      if (v !== null) return v;
    } catch {
      /* fall through to memory */
    }
  }
  return memory.has(full) ? (memory.get(full) as string) : null;
}

export function writeString(key: string, value: string): boolean {
  const full = PREFIX + key;
  memory.set(full, value);
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(full, value);
    return true;
  } catch {
    return false;
  }
}

export function removeKey(key: string): void {
  const full = PREFIX + key;
  memory.delete(full);
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(full);
  } catch {
    /* ignore */
  }
}

/** Read and JSON-parse a value; returns `fallback` when missing or corrupt. */
export function readJSON<T>(key: string, fallback: T): T {
  const raw = readString(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeJSON(key: string, value: unknown): boolean {
  try {
    return writeString(key, JSON.stringify(value));
  } catch {
    return false;
  }
}

/** Test helper: wipe the in-memory fallback. */
export function _resetMemoryStorage(): void {
  memory.clear();
}

// ---------------------------------------------------------------------------
// Small validation helpers shared by the stores
// ---------------------------------------------------------------------------

export function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

export function num(x: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  const n = typeof x === "number" ? x : typeof x === "string" && x.trim() !== "" ? Number(x) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function int(x: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  return Math.round(num(x, fallback, min, max));
}

export function bool(x: unknown, fallback: boolean): boolean {
  return typeof x === "boolean" ? x : fallback;
}

export function str(x: unknown, fallback: string, maxLen = 1000): string {
  return typeof x === "string" ? x.slice(0, maxLen) : fallback;
}

export function oneOf<T extends string>(x: unknown, values: readonly T[], fallback: T): T {
  return typeof x === "string" && (values as readonly string[]).includes(x) ? (x as T) : fallback;
}

/** Random identifier (uses crypto when available). */
export function randomId(len = 16): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const out: string[] = [];
  try {
    const bytes = new Uint8Array(len);
    globalThis.crypto.getRandomValues(bytes);
    for (let i = 0; i < len; i++) out.push(alphabet[bytes[i] % alphabet.length]);
    return out.join("");
  } catch {
    for (let i = 0; i < len; i++) out.push(alphabet[Math.floor(Math.random() * alphabet.length)]);
    return out.join("");
  }
}
